import React, { useState, useEffect } from 'react';
import {
  FileText,
  Shield,
  Bot,
  Check,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Loader
} from 'lucide-react';
import { API_URL } from '../../config/api';

// Icon mapping for document types
const ICONS = {
  terms_of_service: FileText,
  privacy_notice: Shield,
  ai_usage: Bot
};

// Consent checkbox labels
const CONSENT_LABELS = {
  terms_of_service: 'Olen lukenut ja hyväksyn käyttöehdot',
  privacy_notice: 'Olen tutustunut tietosuojaselosteeseen',
  ai_usage: 'Ymmärrän ja hyväksyn AI-toimintojen käytön'
};

export default function ConsentModal({ onComplete, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [consents, setConsents] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch consent documents from database
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await fetch(`${API_URL}/consent-documents/list.php`);
        const data = await response.json();

        if (data.success && data.documents?.length > 0) {
          setDocuments(data.documents);

          // Initialize consent state
          const initialConsents = {};
          data.documents.forEach(doc => {
            initialConsents[doc.consent.type] = false;
          });
          setConsents(initialConsents);
        } else {
          setError('Suostumusdokumentteja ei löytynyt');
        }
      } catch (err) {
        console.error('Error fetching consent documents:', err);
        setError('Verkkovirhe. Yritä uudelleen.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const slide = documents[currentSlide];
  const Icon = slide ? (ICONS[slide.consent?.type] || FileText) : FileText;
  const isLastSlide = currentSlide === documents.length - 1;
  const isFirstSlide = currentSlide === 0;

  const toggleConsent = (type) => {
    setConsents(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const canProceed = () => {
    if (!slide) return false;
    if (slide.consent?.required) {
      return consents[slide.consent.type];
    }
    return true;
  };

  const nextSlide = async () => {
    if (!canProceed()) return;

    if (isLastSlide) {
      await saveConsents();
    } else {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const saveConsents = async () => {
    setSaving(true);
    setError('');

    try {
      // Get CSRF token
      const csrfRes = await fetch(`${API_URL}/get-csrf-token.php`, {
        credentials: 'include'
      });
      const csrfData = await csrfRes.json();
      const csrfToken = csrfData.csrf_token || csrfData.token;

      // Build consents array from documents
      const consentData = documents.map(doc => ({
        type: doc.consent.type,
        version: doc.consent.version,
        granted: consents[doc.consent.type]
      }));

      // Save consents
      const response = await fetch(`${API_URL}/consents/accept.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ consents: consentData })
      });

      const data = await response.json();

      if (data.success) {
        onComplete();
      } else {
        setError(data.message || 'Suostumusten tallennus epäonnistui');
      }
    } catch (err) {
      console.error('Consent save error:', err);
      setError('Verkkovirhe. Yritä uudelleen.');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem'
      }}>
        <Loader size={40} color="#FFEB3B" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8' }}>Ladataan...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state (no documents)
  if (!documents.length || !slide) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem'
      }}>
        <AlertTriangle size={48} color="#ef4444" />
        <p style={{ color: '#fca5a5', textAlign: 'center' }}>
          {error || 'Suostumusdokumentteja ei voitu ladata'}
        </p>
        <button
          onClick={onLogout}
          style={{
            background: '#ef4444',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Kirjaudu ulos
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Centered container with max-width */}
      <div style={{
        width: '100%',
        maxWidth: '700px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.5rem',
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0
      }}>
        {/* Back button */}
        {!isFirstSlide ? (
          <button
            onClick={prevSlide}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '12px',
              padding: '0.5rem 1rem',
              color: '#94a3b8',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            <ChevronLeft size={18} />
            Takaisin
          </button>
        ) : (
          <div style={{ width: '100px' }} />
        )}

        {/* Logo */}
        <div style={{
          width: '40px',
          height: '40px',
          background: '#FFEB3B',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '6px'
        }}>
          <img
            src="/taskmanager/onetrack192.png"
            alt="OneTrack"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>

        {/* Logout button */}
        <button
          onClick={onLogout}
          style={{
            background: 'none',
            border: 'none',
            color: '#ef4444',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0.5rem'
          }}
        >
          Kirjaudu ulos
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        margin: '0 1.5rem 1rem',
        padding: '0.75rem 1rem',
        background: 'rgba(251, 191, 36, 0.1)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <AlertTriangle size={20} color="#fbbf24" />
        <span style={{ color: '#fbbf24', fontSize: '14px' }}>
          Hyväksy suostumukset jatkaaksesi palvelun käyttöä
        </span>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '0 1.5rem',
        overflow: 'hidden'
      }}>
        {/* Title */}
        <h1 style={{
          fontSize: 'clamp(24px, 5vw, 32px)',
          fontWeight: '700',
          color: 'white',
          textAlign: 'center',
          margin: '0.5rem 0 1rem',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem'
        }}>
          <Icon size={28} color="#FFEB3B" />
          {slide.title}
          <span style={{
            fontSize: '12px',
            color: '#64748b',
            fontWeight: '400',
            background: 'rgba(100,116,139,0.2)',
            padding: '0.25rem 0.5rem',
            borderRadius: '6px'
          }}>
            v{slide.consent.version}
          </span>
        </h1>

        {/* Scrollable content */}
        <div style={{
          flex: 1,
          background: 'rgba(15, 23, 42, 0.8)',
          borderRadius: '16px',
          border: '1px solid #334155',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '1.25rem',
            fontSize: '14px',
            lineHeight: '1.7',
            color: '#cbd5e1',
            whiteSpace: 'pre-wrap'
          }}>
            {slide.content}
          </div>
        </div>
      </div>

      {/* Bottom section */}
      <div style={{
        padding: '1rem 1.5rem',
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
        flexShrink: 0
      }}>
        {/* Error message */}
        {error && (
          <div style={{
            padding: '0.75rem 1rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid #ef4444',
            borderRadius: '12px',
            color: '#fca5a5',
            fontSize: '14px',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Required consent checkbox */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: consents[slide.consent.type]
            ? 'rgba(255, 235, 59, 0.1)'
            : 'rgba(255,255,255,0.05)',
          border: `2px solid ${consents[slide.consent.type] ? '#FFEB3B' : '#334155'}`,
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: `2px solid ${consents[slide.consent.type] ? '#FFEB3B' : '#475569'}`,
            background: consents[slide.consent.type] ? '#FFEB3B' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s'
          }}>
            {consents[slide.consent.type] && <Check size={16} color="#0f172a" strokeWidth={3} />}
          </div>
          <span style={{ color: '#e2e8f0', fontSize: '14px', flex: 1 }}>
            {CONSENT_LABELS[slide.consent.type] || `Hyväksyn: ${slide.title}`}
          </span>
          <input
            type="checkbox"
            checked={consents[slide.consent.type] || false}
            onChange={() => toggleConsent(slide.consent.type)}
            style={{ display: 'none' }}
          />
        </label>

        {/* Pagination dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '1rem'
        }}>
          {documents.map((doc, idx) => (
            <div
              key={idx}
              style={{
                width: idx === currentSlide ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: idx === currentSlide ? '#FFEB3B' :
                  idx < currentSlide && consents[doc.consent.type] ? '#22c55e' : '#475569',
                transition: 'all 0.3s'
              }}
            />
          ))}
        </div>

        {/* Action button */}
        <button
          onClick={nextSlide}
          disabled={!canProceed() || saving}
          style={{
            width: '100%',
            padding: '1rem',
            background: canProceed() ? '#FFEB3B' : '#475569',
            color: canProceed() ? '#0f172a' : '#94a3b8',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '700',
            cursor: canProceed() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
            opacity: saving ? 0.7 : 1
          }}
        >
          {saving ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #0f172a',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Tallennetaan...
            </>
          ) : isLastSlide ? (
            'Hyväksy ja jatka'
          ) : (
            <>Seuraava <ChevronRight size={20} /></>
          )}
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </div>
  );
}
